// apps/webapp/webapp/src/redesign/popover-two-column-table/popover-two-columns.tsx

import React from 'react';
import { Box, Divider, Fade, Grid, Stack, Typography, TypographyVariant } from '@mui/material';
import {
  ColoredIcon,
  ContentWrapper,
  HeaderContainer,
  IconWrapper,
  LabelContainer,
  PaperContainer,
  PaperContent,
  StyledPopover,
} from './popover-two-columns-styled-components';

export type PopoverTwoColumnsInfoRow = {
  label: React.ReactNode;
  labelIcon?: React.ReactNode;
  labelVariant?: TypographyVariant;
  value: React.ReactNode;
};

export type PopoverTwoColumnsProps = {
  header: React.ReactNode;
  data: PopoverTwoColumnsInfoRow[][];
  anchorEl?: HTMLElement | null;
  maxWidth?: number | string;
  maxHeight?: number | string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  open?: boolean;
  onClose?: () => void;
};

export const PopoverTwoColumns = ({
  header,
  data,
  maxWidth,
  maxHeight,
  anchorEl,
  open,
  onClose,
  onMouseEnter,
  onMouseLeave,
}: PopoverTwoColumnsProps) => {
  const finalOpen = open !== undefined ? open : Boolean(anchorEl);

  return (
    <StyledPopover
      open={finalOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      disableEnforceFocus={true}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 0 }}
      disableRestoreFocus
      disableAutoFocus={true}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      slotProps={{ paper: { onMouseEnter, onMouseLeave } }}
    >
      <PaperContent width={maxWidth} maxHeight={maxHeight}>
        <PaperContainer>
          <HeaderContainer>{header}</HeaderContainer>

          <ContentWrapper>
            {data.map((groups, groupIndex) => (
              <Grid container spacing={3} key={'group-' + groupIndex}>
                {groups.map((row, idx) => (
                  <Grid key={idx} item xs={12}>
                    <Stack
                      direction="row"
                      spacing={2}
                      justifyContent={'space-between'}
                      alignItems="center"
                    >
                      <LabelContainer>
                        {row.labelIcon ? (
                          <IconWrapper>
                            <ColoredIcon>{row.labelIcon}</ColoredIcon>
                          </IconWrapper>
                        ) : null}

                        <Typography variant={row.labelVariant ?? 'body2'} fontWeight={600}>
                          {row.label}
                        </Typography>
                      </LabelContainer>
                      <Box
                        sx={{
                          flex: 1,
                          display: 'flex',
                          justifyContent: 'flex-end',
                          textAlign: 'right',
                          overflow: 'hidden',
                        }}
                      >
                        {typeof row.value === 'string' ? (
                          <Typography variant="body1" fontWeight={600} textOverflow={'ellipsis'}>
                            {row.value}
                          </Typography>
                        ) : (
                          row.value
                        )}
                      </Box>
                    </Stack>
                  </Grid>
                ))}
                {groupIndex < data.length - 1 && (
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                )}
              </Grid>
            ))}
          </ContentWrapper>
        </PaperContainer>
      </PaperContent>
    </StyledPopover>
  );
};
